<template>
  <div class="p-0 max-lg:max-w-full">
    <div class="container">
      <LazyUiBreadcrumbs />
    </div>
    <div class="lg:flex">
      <div
        class="relative min-h-[200px] border-r border-gray-strokes lg:min-h-[760px] lg:w-[478px]"
      >
        <UiLazyImg
          class="absolute left-0 top-0 h-full w-full"
          :src="pageDataMediaUrl"
          :alt="pageData?.currentTranslation?.title || 'image'"
          cover
          no-lazy
        />
      </div>
      <div class="container flex-1 lg:max-w-none lg:pl-27.5">
        <form
          class="max-w-[500px] space-y-6 py-8 lg:space-y-8 lg:py-20"
          @submit="onSubmit"
        >
          <h1
            v-if="pageData?.currentTranslation?.title"
            class="font-gt-ultra text-3xl font-normal leading-none tracking-normal text-gray-dark"
          >
            {{ pageData?.currentTranslation?.title }}
          </h1>
          <div
            v-if="pageData?.currentTranslation?.description"
            class="text-gray-about [&_p]:font-normal"
            v-html="pageData.currentTranslation.description"
          />
          <div>
            <div class="ui-text-m mb-1 capitalize text-gray-dark">
              {{ $t("orderNumber") }}*
            </div>
            <UiInput
              v-model="form.number"
              name="number"
              :placeholder="'XXXXXXXXXX'"
              :lazy="false"
            />
          </div>
          <div>
            <div class="ui-text-m mb-1 capitalize text-gray-dark">
              {{ $t("orderEmail") }}*
            </div>
            <UiInput
              v-model="form.email"
              name="email"
              :placeholder="'XXXXXXXXXX'"
              :lazy="false"
            />
          </div>
          <div>
            <div class="ui-text-m mb-1 capitalize text-gray-dark">
              {{ $t("billingZipCode") }}*
            </div>
            <UiInput
              v-model="form.billing_zip"
              name="billing_zip"
              :placeholder="'XXXXXXXXXX'"
              :lazy="false"
            />
          </div>
          <div>
            <UiButton
              :loading="isSubmitting"
              :disabled="!meta.valid"
              type="submit"
              color="primary"
              class="px-9 max-lg:w-full"
            >
              {{ $t("requestServiceOrReturn") }}
            </UiButton>
          </div>
          <div
            v-if="pageData?.currentTranslation?.short_description"
            class="text-gray-about [&_p]:font-normal"
            v-html="pageData.currentTranslation.short_description"
          ></div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { object, string } from "yup";
import { useForm } from "vee-validate";
import { FetchError } from "ofetch";

definePageMeta({
  middleware: ["guest-order-tracking"],
});

const { $api } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const localePath = useLocalePathPolyfill();
const { setBreadcrumbs } = useBreadcrumbs();
const { openSystemDialog } = useSystemDialogs();
const generalStore = useGeneralStore();
const { visitEvent } = useEvents();

interface InitialForms {
  email: string;
  number: string;
  billing_zip: string;
}

const { data } = await useAsyncData("track-order", () => {
  return $api.pages.getPage("track-order");
});

const pageData = data.value?.data;

const initialForm: InitialForms = {
  email: "",
  number: "",
  billing_zip: "",
};

const form = reactive(JSON.parse(JSON.stringify(initialForm))) as InitialForms;

const schema = object().shape({
  email: string().email().required().label(t("emailAddress")),
  number: string().required().label(t("orderNumber")),
  billing_zip: string().required().max(10).label(t("billingZipCode")),
});

const { handleSubmit, setErrors, meta, isSubmitting } = useForm({
  initialValues: initialForm,
  validationSchema: schema,
});

const onSubmit = handleSubmit(async (values) => {
  try {
    const data = await $api.orders.getTrackingOrder(values);
    generalStore.setGuestOrderInfo(data?.data);
    await router.push(
      localePath(
        `/orders?email=${values.email}&number=${values.number}&billing_zip=${values.billing_zip}`,
      ),
    );
  } catch (error) {
    if (error instanceof FetchError) {
      setErrors(responseErrorToObjectErrors(error?.data?.errors));

      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Wrong data",
      });
    }
  }
});

const pageDataMediaUrl = computed(() => {
  return (
    getImageByPosition(pageData?.medias || [], "page")?.file?.url ||
    "/images/track_order-correct.png"
  );
});

setBreadcrumbs([
  { text: "Home", href: "/", disabled: false },
  {
    text: pageData?.currentTranslation?.title || t("serviceAndReturns"),
    href: route.path,
    disabled: true,
  },
]);

onMounted(() => {
  visitEvent(pageData?.is_plp);
});
</script>
